import type { DailyRes, ErrorCode, GuideRes, Me, PrevGuess, Quota, SuggestRes, Suggestion, WordRes } from "../shared/api.ts";
import { TAGS, WORD_RE, type Tag, type Word } from "../shared/words.ts";
import { $, ApiError, api, esc, fmtReset, logout } from "./http.ts";

// ---------- 本地儲存 ----------

interface Try { t: string; st: "pending" | "ok" | "off"; tag?: Tag; hint?: string }
interface CardState { hint: number; tries: Try[]; done: boolean; status?: Status }
type Status = "solved" | "revealed" | "looked";
interface Today { day: string; idx: number; cards: Record<string, CardState> }
interface Entry { w: string; day: string; kind: "daily" | "lookup"; status: Status; hints: number; tries: number; at: number; word: Word }

const TODAY_KEY = "ugw-today", HISTORY_KEY = "ugw-history", HISTORY_MAX = 3000;

const load = <T>(key: string): T | null => { try { return JSON.parse(localStorage.getItem(key) ?? "null"); } catch { return null; } };
const store = (key: string, v: unknown) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };

let history: Entry[] = load<Entry[]>(HISTORY_KEY) ?? [];
const saveHistory = () => store(HISTORY_KEY, history);

function record(e: Omit<Entry, "at">) {
  history = history.filter(h => !(h.w === e.w && h.day === e.day && h.kind === e.kind));
  history.unshift({ ...e, at: Date.now() });
  if (history.length > HISTORY_MAX) history.length = HISTORY_MAX;
  saveHistory();
}

const localDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const fmtDay = (day: string) => { const [, m, d] = day.split("-").map(Number); return `${m} 月 ${d} 日`; };

// ---------- 狀態 ----------

let me: Me | null = null;
let daily: DailyRes | null = null;
let today: Today | null = null;
let busy = false, looking = false;
/** 引導停用後，猜測只記下來不送伺服器；offMsg 是要一直顯示的說明。 */
let guideOff = false, offMsg = "";
const AI_OFF = "AI 引導目前沒有開放。可以先寫下想法，再翻答案對照。";

type View = { name: "today" } | { name: "word"; word: Word; from: "lookup" | "history" } | { name: "history" };
let view: View = { name: "today" };
/** 查字和翻紀錄時的卡片狀態（直接翻開）。 */
let looseState: CardState | null = null;

const shortM = (m: string) => m.replace(/（[^）]*）|\([^)]*\)/g, "").trim() || m;
const tagCls = (t: Tag) => t === "hit" || t === "close" ? "s-good" : t === "wrong" ? "s-bad" : "s-mid";
const fresh = (): CardState => ({ hint: 0, tries: [], done: false });

function current(): { word: Word; state: CardState } | null {
  if (view.name === "word") return looseState && { word: view.word, state: looseState };
  if (view.name !== "today" || !daily || !today || !daily.words.length) return null;
  const word = daily.words[today.idx];
  today.cards[word.w] ??= fresh();
  return { word, state: today.cards[word.w] };
}
const saveToday = () => today && store(TODAY_KEY, today);

// ---------- 帳號與額度 ----------

async function loadMe() {
  try { me = await api<Me>("/api/me"); } catch { me = null; }
  if (me && !me.ai) disableGuide(AI_OFF);
  renderAccount();
}

function disableGuide(msg: string) { guideOff = true; offMsg = msg; showNotice(msg); }
function setQuota(q: Quota | undefined) { if (me && q) { me.quota = q; renderAccount(); } }

function quotaText(q: Quota) {
  if (q.remaining == null) return `點數<b>無限</b>`;
  const parts = [`今天還剩 <b>${q.remaining}</b> 點`];
  if (q.bonus > 0) parts.push(`含加值 ${q.bonus} 點`);
  else if (q.daily.limit) parts.push(`${fmtReset(q.resetAt)} 重置`);
  return parts.join("，");
}

function renderAccount() {
  const el = $("account");
  if (!me) { el.hidden = true; return; }
  el.hidden = false;
  const q = me.quota, u = me.user;
  const who = u
    ? `<div class="who">${u.avatar ? `<img class="avatar" src="${esc(u.avatar)}" alt="">` : `<span class="avatar"></span>`}<span>${esc(u.login)}</span></div>`
    : `<div class="who"><span>訪客</span></div>`;
  const acts = u
    ? `${u.isAdmin ? `<a class="btn quiet" href="/admin">後台</a>` : ""}<button class="btn quiet" type="button" id="logout">登出</button>`
    : `<a class="btn gh" href="/auth/github?next=/app" aria-label="GitHub 登入"><span class="gh-text">GitHub 登入</span></a>`;
  el.innerHTML = `${who}<div class="quota${q.remaining === 0 ? " empty" : ""}">${quotaText(q)}${!u ? `<br>登入後點數更多` : ""}</div><div class="acts">${acts}</div>`;
  el.querySelector<HTMLButtonElement>("#logout")?.addEventListener("click", logout);
}

function quotaMessage(q: Quota | undefined, what: string) {
  const when = q ? fmtReset(q.resetAt) : "明天";
  const tail = me?.user ? "" : me?.login ? "登入 GitHub 可以拿到更多點數。" : "";
  return `今天的點數用完了，${what}。點數在${when}重置。${tail}`;
}

function errMsg(code: ErrorCode | "network") {
  if (code === "busy") return "上一個請求還沒好，稍等一下再送。";
  if (code === "ai_busy") return "AI 現在比較忙，稍等一下再送。";
  if (code === "invalid_json") return "AI 的回覆讀不到，請再送一次。";
  if (code === "refused") return "這句沒辦法判斷，換個說法試試。";
  if (code === "not_found") return "伺服器找不到這個字，請重新整理。";
  return "連線中斷了，請再送一次。";
}
function showNotice(t: string) { const n = $("notice"); n.textContent = t; n.hidden = !t; }
function lookupNotice(t: string) { const n = $("lookupNotice"); n.textContent = t; n.hidden = !t; }

// ---------- 每日五個字 ----------

async function loadDaily() {
  try { daily = await api<DailyRes>("/api/daily"); } catch { daily = null; }
  if (!daily) { $("dayLabel").textContent = "載入失敗，請重新整理"; return; }
  const saved = load<Today>(TODAY_KEY);
  today = saved && saved.day === daily.day ? saved : { day: daily.day, idx: 0, cards: {} };
  today.idx = Math.min(today.idx, Math.max(0, daily.words.length - 1));
  saveToday();
  render();
}

function finish(word: Word, state: CardState, status: Status) {
  state.done = true; state.status = status;
  if (view.name === "today" && daily) {
    record({ w: word.w, day: daily.day, kind: "daily", status, hints: state.hint, tries: state.tries.length, word });
    saveToday();
  } else if (view.name === "word" && view.from === "lookup") {
    record({ w: word.w, day: localDay(), kind: "lookup", status, hints: state.hint, tries: state.tries.length, word });
  }
}

function nextUnfinished() {
  if (!daily || !today) return -1;
  const n = daily.words.length;
  for (let k = 1; k <= n; k++) {
    const i = (today.idx + k) % n;
    if (!today.cards[daily.words[i].w]?.done) return i;
  }
  return -1;
}

// ---------- 事件 ----------

$("guessForm").addEventListener("submit", async e => {
  e.preventDefault();
  const cur = current();
  const input = $<HTMLInputElement>("guess"), t = input.value.trim();
  if (!cur || !t || cur.state.done || busy) return;
  const { word, state } = cur;
  const g: Try = { t, st: "pending" };
  state.tries.push(g); input.value = ""; busy = true; render();
  if (guideOff) { g.st = "off"; busy = false; saveToday(); render(); return; }
  showNotice("");
  const prev: PrevGuess[] = state.tries.filter(p => p.st === "ok").map(p => ({ guess: p.t, tag: p.tag!, hint: p.hint ?? "" }));
  try {
    const r = await api<GuideRes>("/api/guide", { w: word.w, guess: t, hint: state.hint, prev });
    g.st = "ok"; g.tag = r.tag; g.hint = r.hint;
    if (r.tag === "hit") finish(word, state, "solved");
    setQuota(r.quota);
  } catch (err) {
    const e = err instanceof ApiError ? err : new ApiError(0, "network");
    setQuota(e.quota);
    if (e.code === "quota" || e.code === "banned" || e.code === "ai_unavailable") {
      g.st = "off";
      disableGuide(e.code === "quota" ? quotaMessage(e.quota, "可以先寫下想法，再翻答案對照")
        : e.code === "banned" ? "這個帳號的 AI 功能已停用。可以先寫下想法，再翻答案對照。"
        : AI_OFF);
    } else {
      state.tries.splice(state.tries.indexOf(g), 1); input.value = t; showNotice(errMsg(e.code));
    }
  }
  busy = false; saveToday(); render();
});

$("lookupForm").addEventListener("submit", async e => {
  e.preventDefault();
  const input = $<HTMLInputElement>("lookupWord"), w = input.value.trim().toLowerCase();
  closeSuggest();
  if (!w || looking) return;
  if (!WORD_RE.test(w)) { lookupNotice("請輸入一個英文單字，只能有字母和連字號。"); return; }
  looking = true;
  const cost = me?.costs.word;
  lookupNotice(`正在拆解 ${w}，新的字大約要 10–30 秒${cost ? `，會用掉 ${cost} 點` : ""}…`);
  render();
  try {
    const r = await api<WordRes>("/api/word", { w });
    setQuota(r.quota);
    input.value = ""; lookupNotice("");
    openWord(r.word, "lookup");
  } catch (err) {
    const e = err instanceof ApiError ? err : new ApiError(0, "network");
    setQuota(e.quota);
    if (e.code === "not_word" || e.code === "bad_request") lookupNotice(e.detail || "這個字沒辦法用字根拆解，換一個試試。");
    else if (e.code === "bad_split" || e.code === "invalid_json") lookupNotice("拆出來的結果對不上這個字，再按一次試試（這次不扣點數）。");
    else if (e.code === "quota") lookupNotice(quotaMessage(e.quota, `查新的字一次要 ${cost ?? "幾"} 點`));
    else if (e.code === "banned") lookupNotice("這個帳號的 AI 功能已停用。");
    else if (e.code === "ai_unavailable") lookupNotice("查新的字需要 AI，目前沒有開放。");
    else lookupNotice(errMsg(e.code));
  }
  looking = false; render();
});

// ---------- 查字的自動完成 ----------

const sInput = $<HTMLInputElement>("lookupWord"), sList = $("suggest");
let sItems: Suggestion[] = [], sActive = -1, sTimer = 0, sSeq = 0;

function closeSuggest() {
  sItems = []; sActive = -1; sList.hidden = true; sList.innerHTML = "";
  sInput.setAttribute("aria-expanded", "false"); sInput.removeAttribute("aria-activedescendant");
}

function renderSuggest() {
  if (!sItems.length) return closeSuggest();
  const q = sInput.value.trim().toLowerCase();
  sList.innerHTML = sItems.map((x, i) => `<li id="sg-${i}" role="option" aria-selected="${i === sActive}" data-i="${i}">
    <span class="sw">${x.w.startsWith(q) ? `<b>${esc(x.w.slice(0, q.length))}</b>${esc(x.w.slice(q.length))}` : esc(x.w)}</span>
    ${x.free ? `<small class="free">已拆過，不扣點</small>` : ""}${x.level ? `<small>大考 ${x.level} 級</small>` : ""}</li>`).join("");
  sList.hidden = false;
  sInput.setAttribute("aria-expanded", "true");
  if (sActive >= 0) { sInput.setAttribute("aria-activedescendant", `sg-${sActive}`); sList.children[sActive]?.scrollIntoView({ block: "nearest" }); }
  else sInput.removeAttribute("aria-activedescendant");
}

sInput.addEventListener("input", () => {
  clearTimeout(sTimer);
  const q = sInput.value.trim();
  if (!q) return closeSuggest();
  sTimer = window.setTimeout(async () => {
    const seq = ++sSeq;
    try {
      const r = await api<SuggestRes>(`/api/suggest?q=${encodeURIComponent(q)}`);
      if (seq !== sSeq) return;
      sItems = r.items; sActive = -1; renderSuggest();
    } catch { closeSuggest(); }
  }, 120);
});

sInput.addEventListener("keydown", e => {
  if (sList.hidden) return;
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    const n = sItems.length;
    sActive = e.key === "ArrowDown" ? (sActive + 1) % n : (sActive - 1 + n) % n;
    renderSuggest();
  } else if (e.key === "Enter" && sActive >= 0) {
    sInput.value = sItems[sActive].w;
  } else if (e.key === "Escape") {
    closeSuggest();
  }
});

// 用 pointerdown 選字，才不會先觸發 blur 把清單關掉。
sList.addEventListener("pointerdown", e => {
  const li = (e.target as HTMLElement).closest<HTMLElement>("[data-i]");
  if (!li) return;
  e.preventDefault();
  sInput.value = sItems[Number(li.dataset.i)].w;
  closeSuggest();
  $<HTMLFormElement>("lookupForm").requestSubmit();
});
sInput.addEventListener("blur", () => setTimeout(closeSuggest, 100));

function openWord(word: Word, from: "lookup" | "history") {
  // 查字跟每日的字一樣先自己猜；從紀錄點開的舊字直接看答案，可以再遮起來猜一次。
  looseState = from === "lookup" ? fresh() : { hint: 2, tries: [], done: true, status: "looked" };
  view = { name: "word", word, from };
  const hash = `#/w/${word.w}`;
  if (location.hash !== hash) history_push(hash);
  render();
  window.scrollTo({ top: 0, behavior: "smooth" });
}
// 換網址但不觸發 hashchange 重新導頁。
const history_push = (hash: string) => window.history.pushState(null, "", hash);

$("tSplit").onclick = () => { const c = current(); if (c && c.state.hint < 1 && !c.state.done && !busy) { c.state.hint = 1; saveToday(); render(); } };
$("tMean").onclick = () => { const c = current(); if (c && c.state.hint < 2 && !c.state.done && !busy) { c.state.hint = 2; saveToday(); render(); } };
$("tReveal").onclick = () => { const c = current(); if (c && !c.state.done && !busy) { finish(c.word, c.state, "revealed"); render(); } };

$("chips").addEventListener("click", e => {
  const li = (e.target as HTMLElement).closest<HTMLElement>("[data-i]");
  if (!li || !today || busy) return;
  today.idx = Number(li.dataset.i); saveToday(); showNotice(offMsg); render();
});

$("nextRow").addEventListener("click", e => {
  if (!(e.target as HTMLElement).closest("#nextBtn") || !today) return;
  const i = nextUnfinished();
  if (i < 0) return;
  today.idx = i; saveToday(); showNotice(offMsg); render();
  window.scrollTo({ top: 0, behavior: "smooth" }); $("guess").focus({ preventScroll: true });
});

// ---------- 路由 ----------

function route() {
  const h = location.hash.replace(/^#/, "");
  if (h === "/history") view = { name: "history" };
  else if (h.startsWith("/w/")) {
    const w = decodeURIComponent(h.slice(3));
    if (view.name === "word" && view.word.w === w) return render();
    const e = history.find(x => x.w === w);
    if (e) { openWord(e.word, "history"); return; }
    view = { name: "today" };
  } else view = { name: "today" };
  showNotice(view.name === "today" ? offMsg : "");
  render();
}
addEventListener("hashchange", route);
addEventListener("popstate", route);

// ---------- 畫面 ----------

function render() {
  const tab = view.name === "history" ? "history" : view.name === "word" && view.from === "history" ? "history" : "today";
  document.querySelectorAll<HTMLAnchorElement>("[data-tab]").forEach(a => a.toggleAttribute("aria-current", a.dataset.tab === tab));
  $<HTMLButtonElement>("lookupBtn").disabled = looking;

  $("todayHead").hidden = view.name !== "today";
  $("back").hidden = !(view.name === "word" && view.from === "history");
  $("history").hidden = view.name !== "history";
  if (view.name === "history") { $("card").hidden = true; renderHistory(); return; }
  if (view.name === "today") renderChips();

  const cur = current();
  $("card").hidden = !cur;
  if (cur) renderCard(cur.word, cur.state);
}

function renderChips() {
  if (!daily || !today) return;
  $("dayLabel").textContent = fmtDay(daily.day);
  $("chips").innerHTML = daily.words.map((x, i) => {
    const s = today!.cards[x.w];
    const cls = [i === today!.idx ? "on" : "", s?.status ?? ""].join(" ");
    const mark = s?.status === "solved" ? "✓" : s?.status === "revealed" ? "看過" : "";
    return `<li data-i="${i}" class="${cls}" role="button" tabindex="0" aria-current="${i === today!.idx}"><span>${esc(x.w)}</span>${mark ? `<small>${mark}</small>` : ""}</li>`;
  }).join("");
}
$("chips").addEventListener("keydown", e => {
  if ((e.key === "Enter" || e.key === " ") && (e.target as HTMLElement).dataset.i) { e.preventDefault(); (e.target as HTMLElement).click(); }
});

function renderCard(x: Word, st: CardState) {
  const meta = [x.pos, x.lv, x.level ? `大考 ${x.level} 級` : ""];
  if (view.name === "today" && daily && today) meta.push(`第 ${today.idx + 1} / ${daily.words.length} 個`);
  else if (view.name === "word") meta.push(view.from === "lookup" ? "查詢" : "紀錄");
  $("meta").innerHTML = meta.filter(Boolean).map(m => `<span>${esc(m)}</span>`).join("");

  const w = $("word");
  const showSplit = st.hint >= 1 || st.done, showMean = st.hint >= 2 || st.done;
  const key = x.w + "|" + (showSplit ? "s" : "") + (showMean ? "m" : "");
  if (w.dataset.k !== key) {
    const wasWhole = w.dataset.k === x.w + "|";
    w.dataset.k = key;
    w.classList.remove("is-split", "is-meant");
    if (!showSplit) {
      // 還沒要求拆字前只放一個純文字節點，選取文字也看不出切法。
      w.textContent = x.w;
    } else {
      w.innerHTML = x.parts.map(p => `<span class="part"><span class="ptext">${esc(p[0])}</span>${showMean ? `<span class="pmean">${esc(shortM(p[1]))}</span>` : ""}</span>`).join("");
      if (wasWhole) void w.offsetWidth;
      w.classList.add("is-split"); if (showMean) w.classList.add("is-meant");
    }
  }
  fitWord(x);

  $("guessForm").hidden = st.done;
  $<HTMLButtonElement>("send").disabled = busy;
  $<HTMLButtonElement>("send").textContent = guideOff ? "記下來" : "問方向";

  $("tries").innerHTML = st.tries.map((g, i) => {
    let fb = "";
    if (g.st === "pending") fb = `<span class="dots">AI 想一下</span>`;
    else if (g.st === "off") fb = `<span class="s-wait">沒有送出給 AI</span>`;
    else fb = `<span class="tag ${tagCls(g.tag!)}">${TAGS[g.tag!]}</span><span class="hint">${esc(g.hint ?? "")}</span>`;
    return `<li class="try"><div class="try-text"><span class="g-idx">#${i + 1}</span>${esc(g.t)}</div><div class="try-fb">${fb}</div></li>`;
  }).join("");

  $("tools").hidden = st.done;
  $<HTMLButtonElement>("tSplit").disabled = st.hint >= 1 || busy;
  $<HTMLButtonElement>("tMean").disabled = st.hint >= 2 || busy;
  $<HTMLButtonElement>("tReveal").disabled = busy;

  const a = $("answer");
  a.hidden = !st.done;
  if (st.done) {
    const result = st.status === "solved"
      ? (st.hint ? `用了${st.hint === 1 ? "拆字" : "字根"}提示，第 ${st.tries.length} 次猜到` : `沒看提示，第 ${st.tries.length} 次猜到`)
      : st.status === "revealed" ? "直接看了答案" : "";
    a.innerHTML = `
      ${result ? `<p class="result ${st.status === "solved" ? "s-good" : "s-wait"}">${result}</p>` : ""}
      ${view.name === "word" && view.from === "history" && st.status === "looked" ? `<button class="btn quiet" type="button" id="retry">遮起來再猜一次</button>` : ""}
      ${x.custom ? `<p class="note">這張卡由 AI 拆解，字源可能有誤，有疑問請查字典確認。</p>` : ""}
      <div><div class="ans-k">意思</div><p class="ans-mean">${esc(x.ans)}</p></div>
      <div class="formula">${x.parts.map((p, i) => `<span class="f" style="--hl:var(--hl-${i % 4 + 1})">${esc(p[0])}<span>${esc(p[1])}</span></span>`).join(`<span class="plus">+</span>`)}</div>
      <div><div class="ans-k">意義重點</div><div class="chips-s">${x.pts.map(p => `<span class="chip">${esc(p)}</span>`).join("")}</div></div>
      ${x.note ? `<p class="note">${esc(x.note)}</p>` : ""}
      <div><div class="ans-k">同字根的字</div><div class="family">${x.fam.map(f => `<div class="fam"><code>${esc(f[0])}</code><span>${esc(f[1])}</span></div>`).join("")}</div></div>
      ${x.tip ? `<div class="tip"><div class="ans-k">怎麼記</div><p>${esc(x.tip)}</p></div>` : ""}`;
    a.querySelector<HTMLButtonElement>("#retry")?.addEventListener("click", () => {
      if (view.name !== "word") return;
      view = { name: "word", word: view.word, from: "lookup" };
      looseState = fresh(); showNotice(offMsg); render();
      $("guess").focus({ preventScroll: true });
    });
  }

  const nr = $("nextRow");
  nr.hidden = !(st.done && view.name === "today");
  if (!nr.hidden) {
    const i = nextUnfinished();
    nr.innerHTML = i >= 0
      ? `<button class="btn primary next" id="nextBtn" type="button">下一個字</button>`
      : `<p class="done-all">今天的五個字都完成了。明天 00:00 換新的一組，看過的字都在 <a href="#/history">紀錄</a> 裡。</p>`;
  }
}

function renderHistory() {
  const el = $("history");
  if (!history.length) {
    el.innerHTML = `<h1>紀錄</h1><p class="note">還沒有紀錄。完成今天的字或查一個字，就會記在這台裝置上。</p>`;
    return;
  }
  const groups = new Map<string, Entry[]>();
  for (const e of history) groups.set(e.day, [...(groups.get(e.day) ?? []), e]);
  const label = (e: Entry) => (e.kind === "lookup" ? "查詢・" : "")
    + (e.status === "solved" ? (e.hints ? "提示後猜到" : "自己猜到") : "看了答案");
  const solved = history.filter(e => e.status === "solved").length;
  el.innerHTML = `
    <div class="h-head"><h1>紀錄</h1>
      <div class="h-acts"><button class="btn quiet" type="button" id="hExport">匯出</button><button class="btn quiet" type="button" id="hClear">清除</button></div></div>
    <p class="note">存在這台裝置的瀏覽器裡，共 ${new Set(history.map(e => e.w)).size} 個字，其中 ${solved} 次是自己猜到的。</p>
    ${[...groups].map(([day, list]) => `
      <section class="h-day"><h2>${fmtDay(day)}</h2><ul>
        ${list.map(e => `<li><a href="#/w/${esc(e.w)}"><code>${esc(e.w)}</code><span class="h-mean">${esc(e.word.ans)}</span>
          <span class="tag ${e.status === "solved" ? "s-good" : "s-wait"}">${label(e)}</span></a></li>`).join("")}
      </ul></section>`).join("")}`;
  $("hExport").onclick = () => {
    const blob = new Blob([JSON.stringify(history, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `you-gist-what-${localDay()}.json` });
    a.click(); URL.revokeObjectURL(a.href);
  };
  $("hClear").onclick = () => {
    if (!confirm("要清除這台裝置上的所有紀錄嗎？清除後沒辦法復原。")) return;
    history = []; saveHistory(); render();
  };
}

// 縮小字級，讓每一段（和底下的意思）都排在同一行。
let meantOff = false;
function fitWord(x: Word) {
  const w = $("word");
  const W = w.clientWidth || 340;
  const max = Math.min(57.6, Math.max(33.6, W * 0.13));
  const split = w.classList.contains("is-split"), meant = w.classList.contains("is-meant");
  const label = parseFloat(getComputedStyle(document.body).fontSize || "16") * 0.9;
  const need = (f: number) => !split ? x.w.length * 0.6 * f
    : x.parts.reduce((s, p) => s + Math.max(p[0].length * 0.6 * f + 0.2 * f, meant && !meantOff ? shortM(p[1]).length * label + 6 : 0), 0) + (x.parts.length - 1) * 0.4 * f;
  let fs = max; while (fs > 18 && need(fs) > W * 0.92) fs -= 1;
  // 段數太多、字底下塞不下意思時，改成列在下面。
  const list = meant && fs < 28;
  w.classList.toggle("is-list", list);
  if (list) { meantOff = true; fs = max; while (fs > 18 && need(fs) > W * 0.92) fs -= 1; meantOff = false; }
  w.style.fontSize = fs + "px";
  const lg = $("legend");
  lg.hidden = !list;
  if (list) lg.innerHTML = x.parts.map((p, i) => `<dt style="--hl:var(--hl-${i % 4 + 1})">${esc(p[0])}</dt><dd>${esc(shortM(p[1]))}</dd>`).join("");
}
addEventListener("resize", () => { const c = current(); if (c) fitWord(c.word); });

route();
loadMe();
loadDaily();
